import { useState } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown, Hash, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MetricCodeBadge } from "@/components/common";
import { useMediaQuery } from "@/hooks";
import { CATEGORY_META } from "../schemas/custom-framework.schema";
import {
  buildMetaParts,
  groupByCategory,
  groupByTopic,
} from "../domains/disclosureMeta";
import { EmptyState } from "./EmptyState";

// Matches Tailwind's `sm` breakpoint — keep in sync with the className below.
const TWO_COLUMN_QUERY = "(min-width: 640px)";

const CATEGORY_STYLES = Object.fromEntries(
  CATEGORY_META.map(({ key, bg, text, gradientFrom }) => [
    key,
    { bg, text, gradientFrom },
  ]),
);

function MetaField({ label, value }) {
  return (
    <Badge variant="outline">
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium text-foreground">{value}</span>
    </Badge>
  );
}

function DisclosureRow({ d, onEdit, onDelete }) {
  const metaParts = buildMetaParts(d);

  return (
    <div className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30">
      <div className="w-20 shrink-0 pt-0.5">
        <MetricCodeBadge code={d.code} category={d.category} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground leading-snug">
          {d.title}
        </span>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {metaParts.map((part) => (
            <MetaField key={part.key} label={part.label} value={part.value} />
          ))}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={() => onEdit(d)}
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => onDelete(d)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function TopicSection({ topic, items, onEdit, onDelete }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-between gap-2 px-4 py-2 text-left hover:bg-muted/20"
      >
        <span className="text-sm font-medium uppercase text-foreground">
          {topic}
        </span>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {items.length}
          </Badge>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
      {open && (
        <div className="flex flex-col divide-y divide-border border-t border-border">
          {items.map((d) => (
            <DisclosureRow
              key={d._id}
              d={d}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryPanel({ categoryKey, items, onEdit, onDelete }) {
  const style = CATEGORY_STYLES[categoryKey] ?? CATEGORY_STYLES.Other;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card">
      {/* Panel header */}
      <div
        className={`flex items-center justify-between border-b border-border bg-gradient-to-b ${style.gradientFrom} to-transparent px-4 py-3`}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg font-medium text-foreground">
            {categoryKey}
          </span>
        </div>
        <Badge className={`${style.bg} ${style.text} border-0 text-sm`}>
          {items.length}
        </Badge>
      </div>

      {/* Topic sub-groups */}
      <div className="flex flex-col divide-y divide-border">
        {groupByTopic(items).map(({ key, items: topicItems }) => (
          <TopicSection
            key={key}
            topic={key}
            items={topicItems}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

export const DisclosureTable = observer(function DisclosureTable({
  disclosures,
  onEdit,
  onDelete,
}) {
  const isTwoColumn = useMediaQuery(TWO_COLUMN_QUERY);

  if (!disclosures.length) {
    return (
      <EmptyState
        icon={Hash}
        title="No metrics yet"
        description='Click "Add Metric" to define your first KPI.'
      />
    );
  }

  const panels = groupByCategory(disclosures);
  const leftPanels = panels.filter((_, i) => i % 2 === 0);
  const rightPanels = panels.filter((_, i) => i % 2 === 1);

  const renderPanel = ({ key, items }) => (
    <CategoryPanel
      key={key}
      categoryKey={key}
      items={items}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );

  // Only one tree is ever mounted (gated on TWO_COLUMN_QUERY, matching the
  // `sm` breakpoint below) rather than rendering both and toggling CSS
  // visibility — each CategoryPanel/TopicSection holds its own local
  // collapse state, so mounting both would let a section's expand/collapse
  // silently diverge between the two copies when the viewport crosses the
  // breakpoint.
  if (!isTwoColumn) {
    return <div className="flex flex-col gap-4">{panels.map(renderPanel)}</div>;
  }

  return (
    <div className="flex gap-4">
      <div className="flex flex-1 flex-col gap-4">
        {leftPanels.map(renderPanel)}
      </div>
      <div className="flex flex-1 flex-col gap-4">
        {rightPanels.map(renderPanel)}
      </div>
    </div>
  );
});
